import { lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Shell from "./components/Shell.jsx";
import RequireCap from "./components/RequireCap.jsx";
import Login from "./pages/Login.jsx";
import Register from "./pages/Register.jsx";

/* In-Shell pages are lazy-loaded so each route ships its own chunk instead of
 * one 500 kB+ app bundle (heavy pages — Chart/Heatmap/Docs/workflow editor —
 * only download when visited). The Shell's <Suspense> shows a spinner while a
 * chunk loads. Public auth pages (Login/Register) stay eager: they are the
 * first paint for a logged-out user and sit outside the Shell/Suspense. */
const Home = lazy(() => import("./pages/Home.jsx"));
const Account = lazy(() => import("./pages/Account.jsx"));
const SettingsKeys = lazy(() => import("./pages/SettingsKeys.jsx"));
const SettingsTelegram = lazy(() => import("./pages/SettingsTelegram.jsx"));
const Notifications = lazy(() => import("./pages/Notifications.jsx"));
const Users = lazy(() => import("./pages/Users.jsx"));
const Docs = lazy(() => import("./pages/Docs.jsx"));
const Obsidian = lazy(() => import("./pages/Obsidian.jsx"));
const Positions = lazy(() => import("./pages/Positions.jsx"));
const MaPositions = lazy(() => import("./pages/MaPositions.jsx"));
const Heatmap = lazy(() => import("./pages/Heatmap.jsx"));
const Admin = lazy(() => import("./pages/Admin.jsx"));
const Chart = lazy(() => import("./pages/Chart.jsx"));
const Trading = lazy(() => import("./pages/Trading.jsx"));
const TradingBot = lazy(() => import("./pages/TradingBot.jsx"));
const MaTrading = lazy(() => import("./pages/MaTrading.jsx"));
const MaTradingView = lazy(() => import("./pages/MaTradingView.jsx"));
const MaWorkflowEditor = lazy(() => import("./pages/MaWorkflowEditor.jsx"));
const Alerts = lazy(() => import("./pages/Alerts.jsx"));
const MultiAlerts = lazy(() => import("./pages/MultiAlerts.jsx"));
const CopyTrading = lazy(() => import("./pages/CopyTrading.jsx"));
const CopyTradeAlerts = lazy(() => import("./pages/CopyTradeAlerts.jsx"));
const Scalping = lazy(() => import("./pages/Scalping.jsx"));
const ScalpingView = lazy(() => import("./pages/ScalpingView.jsx"));
const ScalpPositions = lazy(() => import("./pages/ScalpPositions.jsx"));

/* Route table — mirrors the FastAPI page routes / shell NAV. Public auth pages
 * sit outside the Shell; everything else renders inside it (sidenav + gating).
 * Pages still being ported render a Stub so the nav + layout already work. */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      <Route element={<Shell />}>
        <Route index element={<Home />} />
        <Route path="/account" element={<Account />} />

        <Route
          path="/heatmap"
          element={
            <RequireCap cap="heatmap.view">
              <Heatmap />
            </RequireCap>
          }
        />
        <Route
          path="/chart"
          element={
            <RequireCap cap="chart.view">
              <Chart />
            </RequireCap>
          }
        />
        <Route
          path="/alerts"
          element={
            <RequireCap cap="alerts.view">
              <Alerts />
            </RequireCap>
          }
        />
        <Route
          path="/multi-alerts"
          element={
            <RequireCap cap="multi_alerts.view">
              <MultiAlerts />
            </RequireCap>
          }
        />
        <Route
          path="/notifications"
          element={
            <RequireCap cap="templates.view">
              <Notifications />
            </RequireCap>
          }
        />
        <Route
          path="/trading"
          element={
            <RequireCap cap="trading.view">
              <Trading />
            </RequireCap>
          }
        />
        <Route
          path="/trading/bot/:id"
          element={
            <RequireCap cap="trading.view">
              <TradingBot />
            </RequireCap>
          }
        />
        <Route
          path="/ma-trading"
          element={
            <RequireCap cap="ma_trading.view">
              <MaTrading />
            </RequireCap>
          }
        />
        <Route
          path="/ma-trading/positions"
          element={
            <RequireCap cap="ma_trading.view">
              <MaPositions />
            </RequireCap>
          }
        />
        <Route
          path="/ma-trading/new"
          element={
            <RequireCap cap="ma_trading.manage">
              <MaWorkflowEditor />
            </RequireCap>
          }
        />
        <Route
          path="/ma-trading/bot/:id"
          element={
            <RequireCap cap="ma_trading.view">
              <MaWorkflowEditor />
            </RequireCap>
          }
        />
        <Route
          path="/ma-trading/bot/:id/view"
          element={
            <RequireCap cap="ma_trading.view">
              <MaTradingView />
            </RequireCap>
          }
        />
        <Route
          path="/positions"
          element={
            <RequireCap cap="trading.view">
              <Positions />
            </RequireCap>
          }
        />
        <Route
          path="/copy-trading"
          element={
            <RequireCap cap="copy_trading.view">
              <CopyTrading />
            </RequireCap>
          }
        />
        <Route
          path="/copy-trade-alerts"
          element={
            <RequireCap cap="copy_trading.view">
              <CopyTradeAlerts />
            </RequireCap>
          }
        />
        <Route
          path="/scalping"
          element={
            <RequireCap cap="scalping.view">
              <Scalping />
            </RequireCap>
          }
        />
        <Route
          path="/scalping/positions"
          element={
            <RequireCap cap="scalping.view">
              <ScalpPositions />
            </RequireCap>
          }
        />
        <Route
          path="/scalping/bot/:id/view"
          element={
            <RequireCap cap="scalping.view">
              <ScalpingView />
            </RequireCap>
          }
        />
        <Route
          path="/settings/telegram"
          element={
            <RequireCap cap="telegram.view">
              <SettingsTelegram />
            </RequireCap>
          }
        />
        <Route
          path="/settings/keys"
          element={
            <RequireCap cap="keys.view">
              <SettingsKeys />
            </RequireCap>
          }
        />
        <Route
          path="/docs"
          element={
            <RequireCap cap="docs.view">
              <Docs />
            </RequireCap>
          }
        />
        <Route
          path="/users"
          element={
            <RequireCap cap="users.view">
              <Users />
            </RequireCap>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireCap cap="users.manage">
              <Admin />
            </RequireCap>
          }
        />
        <Route
          path="/obsidian"
          element={
            <RequireCap cap="users.manage">
              <Obsidian />
            </RequireCap>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
